/**
 * Mech definition schema, the full BUILD_PLAN section 6 shape: chassis and movement (Phase 2),
 * plus hp, armor, shields, heat, weapons, abilities, and the economy fields (Phase 3). Balance
 * overrides from the admin panel layer over these JSON defaults later.
 */
import { z } from 'zod';
const finite = z.number().finite();
const positive = finite.positive();
const nonNegative = finite.nonnegative();
const id = z.string().regex(/^[a-z][a-z0-9-]*$/);
export const MechClassSchema = z.enum(['light', 'medium', 'heavy']);
export const MechDefinitionSchema = z
    .object({
    id,
    name: z.string().min(1),
    class: MechClassSchema,
    role: z.string().min(1),
    /** Processed asset id of the visual model (assets/processed/mechs/<chassis>/mech.glb). */
    chassis: z.string().regex(/^[a-z0-9][a-z0-9-]*$/),
    /** Accent colour for the placeholder tint and the HUD, as #rrggbb. */
    accent: z.string().regex(/^#[0-9a-f]{6}$/),
    weightTons: positive,
    capsule: z
        .object({
        radius: positive,
        /** Total capsule height, feet to crown. */
        height: positive,
        stepHeight: nonNegative,
        snapDistance: nonNegative,
    })
        .strict(),
    hp: positive,
    /** Damage reduction per location, 0..0.8 (DECISIONS.md #14). */
    armor: z
        .object({
        legs: finite.min(0).max(0.8),
        torso: finite.min(0).max(0.8),
        arms: finite.min(0).max(0.8),
    })
        .strict(),
    shield: nonNegative,
    /** Shield points per second once regeneration resumes. */
    shieldRegen: nonNegative,
    heatCap: positive,
    /** Heat removed per second. */
    heatDissipation: positive,
    /** Walking speed, m/s. */
    speed: positive,
    boostSpeed: positive,
    /** Energy per second while boosting. */
    boostCost: nonNegative,
    /** Energy needed to start boosting (prevents stutter at zero energy). */
    boostEngageEnergy: nonNegative,
    /** Degrees per second. */
    turnRate: positive,
    acceleration: positive,
    deceleration: positive,
    /** Fraction of acceleration available in the air. */
    airControl: finite.min(0).max(1),
    energy: positive,
    energyRegen: nonNegative,
    jumpJets: z
        .object({
        launchVelocity: positive,
        energyCost: nonNegative,
        thrust: nonNegative,
        thrustEnergyPerSecond: nonNegative,
    })
        .strict(),
    weapons: z
        .array(z.object({ slot: z.enum(['primary', 'secondary']), id }).strict())
        .min(1)
        .max(2),
    /** Melee weapon id; omitted mechs use the balance default bash (DECISIONS.md #15). */
    melee: id.optional(),
    /** Weapons the pilot may fit instead of the stock ones (Phase 8): the primary slot only. */
    alternates: z
        .object({ primary: z.array(id).max(3) })
        .strict()
        .default({ primary: [] }),
    abilities: z.array(z.object({ id, cooldown: positive }).strict()).length(1),
    ultimate: id,
    slots: z
        .object({ module: z.number().int().nonnegative(), cosmetic: z.number().int().nonnegative() })
        .strict(),
    unlockCost: z.number().int().nonnegative(),
    starter: z.boolean(),
    lore: z.string().min(1),
    /** Ids this mech is expected to beat / lose to (balance report only). */
    counters: z.object({ hard: z.array(id), soft: z.array(id) }).strict(),
})
    .strict()
    .superRefine((def, ctx) => {
    const slots = def.weapons.map((w) => w.slot);
    if (new Set(slots).size !== slots.length) {
        ctx.addIssue({ code: 'custom', message: `mech "${def.id}": duplicate weapon slots` });
    }
    if (def.boostSpeed <= def.speed) {
        ctx.addIssue({ code: 'custom', message: `mech "${def.id}": boostSpeed must exceed speed` });
    }
})
    .transform((def) => ({ ...def, turnRateRad: (def.turnRate * Math.PI) / 180 }));
//# sourceMappingURL=mech-schema.js.map